import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { decrypt, encrypt } from "../utils/cryptoUtils";
import { makeAuthenticatedRequest } from "../utils/api";
import { Profilehistry } from "../redux/ProfileSlice";
import "./Dashboard.css";

import {
  Building,
  ShieldCheck,
  Users,
  Copy,
  Check,
  Globe,
  Mail,
  Phone,
  Award,
  ShieldAlert,
  Lock,
} from "lucide-react";

const tabs = [
  { id: "business", label: "Business Details", icon: Building },
  { id: "compliance", label: "Compliance & Identity", icon: ShieldCheck },
  { id: "representative", label: "Authorized Contacts", icon: Users },
];

const businessTypeLabels = {
  1: "Individual",
  2: "LLP",
  3: "Partnership",
  4: "Private Limited",
  5: "Proprietorship",
};

const businessCategoryLabels = {
  1: "B2B",
  2: "Digital goods",
  3: "Ecommerce",
  4: "Education",
  5: "Proprietorship",
  6: "Financial Services",
};


// Reusable Detail Component with copy support
function InfoItem({ label, value, copyKey, copiedField, onCopy }) {
  const isCopied = copiedField === copyKey;
  return (
    <div className="group rounded-2xl bg-white/40 backdrop-blur-sm p-4 border border-slate-100/80 hover:bg-white/80 hover:border-indigo-200 hover:shadow-sm transition duration-300 flex flex-col justify-between relative overflow-hidden">
      {/* Subtle corner light reflection */}
      <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-indigo-500/5 to-transparent rounded-bl-full pointer-events-none" />

      <div>
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">
          {label}
        </p>
        <p className="text-sm font-extrabold text-slate-800 break-words font-sans">
          {value || (
            <span className="text-slate-400 font-normal italic">
              Not provided
            </span>
          )}
        </p>
      </div>

      {copyKey && value && (
        <div className="mt-3 flex justify-end">
          <button
            onClick={() => onCopy(value, copyKey)}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-bold border transition duration-200 ${
              isCopied
                ? "bg-emerald-50 border-emerald-200 text-emerald-600 shadow-sm shadow-emerald-100/50"
                : "bg-slate-50/50 border-slate-200 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
            }`}
          >
            {isCopied ? (
              <>
                <Check className="w-3 h-3 text-emerald-500 animate-bounce" />
                <span>Copied!</span>
              </>
            ) : (
              <>
                <Copy className="w-3 h-3 text-slate-400 group-hover:scale-105 duration-200" />
                <span>Copy</span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}

const Profile = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false); // Add loading state
  const [copiedField, setCopiedField] = useState(null);

  const users = JSON.parse(sessionStorage.getItem("users"));
  const user_id = users?.userdatils?.kycs_step ? users.userdatils.user_id : "0";
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const merchantprofile = useSelector((state) => state.profile.profilelist);

  // Memoize the profile data to prevent unnecessary re-renders
  const profile = useMemo(() => {
    return merchantprofile && merchantprofile.length > 0
      ? merchantprofile[1]
      : [];
  }, [merchantprofile]);

  const handleCopy = (text, key) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedField(key);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const fetchprofile = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const requestData = JSON.stringify({
        Dname: "merchant",
        Tname: "kycs",
        Cname: [
          "user_id",
          "step",
          "merchant_id",
          "country",
          "contact_name",
          "contact_number",
          "contact_email",
          "business_cname",
          "business_email",
          "business_type",
          "business_category",
          "business_description",
          "app_link",
          "activate_status",
          "business_name",
          "pincode",
          "city",
          "address",
          "state",
          "pan_number",
          "gst_number",
          "cin_number",
          "aadhar_number",
          "tan_number",
          "status",
        ],
        Where: [
          {
            column: "user_id",
            operator: "=",
            value: user_id,
          },
        ],
        Order: { id: "desc" },
        Limit: "10",
        Offset: "0",
      });

      const encrypted = encrypt(requestData);
      const data = await makeAuthenticatedRequest(
        "POST",
        "/fetch",
        { encrypted },
        navigate,
      );

      const decryptdata = decrypt(data);
      const fixedString = decryptdata.replace(/\\"/g, '"');
      const jsonArray = JSON.parse(fixedString);
      dispatch(Profilehistry(jsonArray));
    } catch (err) {
      setError(
        "Failed to fetch data: " + (err.response?.data?.message || err.message),
      );
    } finally {
      setIsLoading(false);
    }
  }, [user_id, dispatch, navigate]);

  useEffect(() => {
    // Only fetch if we don't already have data
    if (!merchantprofile || merchantprofile.length === 0) {
      fetchprofile();
    }
  }, [fetchprofile, merchantprofile]); // Proper dependencies

  if (isLoading) {
    return (
      <main className="main-content animate-fade-in-up mesh-bg min-h-screen relative overflow-hidden flex items-center justify-center">
        <div className="glass-panel p-8 rounded-3xl flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm font-bold text-slate-650">
            Loading profile details...
          </p>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="main-content animate-fade-in-up mesh-bg min-h-screen relative overflow-hidden flex items-center justify-center">
        <div className="glass-panel p-8 rounded-3xl border border-red-200 bg-red-50/50 max-w-md text-center">
          <ShieldAlert className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <p className="text-sm font-bold text-red-650 mb-4">{error}</p>
          <button
            onClick={fetchprofile}
            className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-red-600 to-red-700 text-white font-bold text-xs shadow-md shadow-red-500/10 hover:brightness-105 transition"
          >
            Retry Fetching
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="main-content animate-fade-in-up mesh-bg min-h-screen relative overflow-hidden pb-10">
      {/* Decorative Floating Color Orbs */}
      <div className="color-orb-1 top-10 left-10 pointer-events-none" />
      <div className="color-orb-2 bottom-20 right-10 pointer-events-none" />

      {/* Header Section */}
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between bg-gradient-to-r from-indigo-900 via-indigo-950 to-slate-950 rounded-3xl px-8 py-6 shadow-xl relative overflow-hidden border border-slate-900 animate-fade-in-up">
        {/* Ambient light leaks inside header */}
        <div className="absolute -top-24 -left-24 w-48 h-48 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -right-24 w-48 h-48 bg-purple-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10">
          <h2 className="text-3xl font-extrabold text-white tracking-tight flex items-center gap-2">
            <span>Merchant Profile</span>
            <span className="text-xs px-2.5 py-0.5 bg-indigo-500/25 border border-indigo-500/35 text-indigo-300 rounded-full font-bold uppercase tracking-wider animate-pulse">
              Verified
            </span>
          </h2>
          <p className="text-indigo-200/80 text-sm mt-1.5 font-medium">
            Review business identity details, compliance status, and system
            configurations.
          </p>
        </div>
        <nav className="mt-4 md:mt-0 relative z-10">
          <ol className="flex items-center space-x-2 text-xs font-semibold">
            <li>
              <Link
                to="/dashboard"
                className="flex items-center gap-1.5 py-2 px-3 bg-white/5 hover:bg-white/10 rounded-xl text-indigo-200 hover:text-white transition duration-200 border border-white/5"
              >
                <span className="material-icons text-sm">dashboard</span>
                <span>Console</span>
              </Link>
            </li>
            <li className="text-indigo-400">/</li>
            <li className="text-indigo-300/60 font-normal">Profile</li>
          </ol>
        </nav>
      </div>

      <div className="container mx-auto relative z-10 space-y-6 animate-fade-in-up">
        {/* ================= TOP BUSINESS SUMMARY ================= */}
        <div className="glass-panel rounded-3xl border border-slate-100 p-6 relative overflow-hidden">
          {/* Accent top gradient line */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-600 via-indigo-500 to-violet-600"></div>
          {/* Ambient inner soft background glow */}
          <div className="absolute -right-24 -bottom-24 w-64 h-64 bg-indigo-500/5 rounded-full blur-3xl pointer-events-none" />

          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 relative z-10">
            {/* Business Identity */}
            <div className="flex items-center gap-5">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-r from-indigo-600 to-violet-600 flex items-center justify-center text-lg font-extrabold text-white shadow-lg shadow-indigo-200/50 flex-shrink-0 relative overflow-hidden">
                {/* Visual gloss overlay */}
                <div className="absolute inset-0 bg-white/10 rotate-12 translate-y-2 pointer-events-none" />
                {profile.business_name
                  ?.split(" ")
                  .map((w) => w[0])
                  .join("")
                  .substring(0, 3)
                  .toUpperCase()}
              </div>

              <div>
                <h1 className="text-2xl font-extrabold text-slate-800 tracking-tight leading-tight">
                  {profile.business_name}
                </h1>
                <div className="flex items-center flex-wrap gap-2.5 mt-1.5">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                    Merchant ID •{" "}
                    <span className="font-mono text-slate-650 bg-slate-100 px-1.5 py-0.5 rounded">
                      {profile.merchant_id}
                    </span>
                  </p>

                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-lg text-[9px] font-extrabold border ${
                      profile.activate_status === "2"
                        ? "bg-emerald-50 text-emerald-600 border-emerald-100 pulse-badge-green"
                        : profile.activate_status === "1"
                          ? "bg-blue-50 text-blue-600 border-blue-100 pulse-badge-blue"
                          : profile.activate_status === "4"
                            ? "bg-red-50 text-red-600 border-red-100 pulse-badge-red"
                            : "bg-emerald-50 text-emerald-600 border-emerald-100 pulse-badge-green"
                    }`}
                  >
                    <span
                      className={`w-1.5 h-1.5 rounded-full mr-1 animate-pulse ${
                        profile.activate_status === "2"
                          ? "bg-emerald-500"
                          : profile.activate_status === "1"
                            ? "bg-blue-500"
                            : profile.activate_status === "4"
                              ? "bg-red-500"
                              : "bg-emerald-500"
                      }`}
                    />

                    {profile.activate_status === "2"
                      ? "Verified"
                      : profile.activate_status === "1"
                        ? "Under Review"
                        : profile.activate_status === "4"
                          ? "Rejected"
                          : "Verified"}
                  </span>
                </div>
              </div>
            </div>

            {/* Quick Support Triggers */}
            <div className="flex flex-wrap gap-2">
              <a
                href={profile.app_link}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 rounded-xl border border-slate-200 bg-white/80 hover:border-indigo-300 hover:text-indigo-600 text-xs font-bold transition shadow-sm hover:shadow active:scale-95 duration-200 flex items-center gap-1.5 text-slate-600"
              >
                <Globe className="w-3.5 h-3.5" />
                <span>Website</span>
              </a>

              <a
                href={`mailto:${profile.contact_email}`}
                className="px-4 py-2 rounded-xl border border-slate-200 bg-white/80 hover:border-indigo-300 hover:text-indigo-600 text-xs font-bold transition shadow-sm hover:shadow active:scale-95 duration-200 flex items-center gap-1.5 text-slate-600"
              >
                <Mail className="w-3.5 h-3.5" />
                <span>Email Support</span>
              </a>

              <a
                href={`tel:${profile.contact_number}`}
                className="px-4 py-2 rounded-xl border border-slate-200 bg-white/80 hover:border-indigo-300 hover:text-indigo-600 text-xs font-bold transition shadow-sm hover:shadow active:scale-95 duration-200 flex items-center gap-1.5 text-slate-600"
              >
                <Phone className="w-3.5 h-3.5" />
                <span>Call Support</span>
              </a>
            </div>
          </div>
        </div>

        {/* ================= LAYOUT SPLIT PORTAL ================= */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          {/* LEFT SYSTEM CONFIGURATIONS */}
          <div className="lg:col-span-4 space-y-6">
            {/* STICKY CARD: TIMELINE & STATUS GAUGE */}
            <div className="glass-panel rounded-3xl border border-slate-100 p-6 space-y-6 relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-500 to-purple-600"></div>

              <div>
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">
                  Onboarding Progress
                </h3>
                <h2 className="text-lg font-extrabold text-slate-800">
                  Verification Lifecycle
                </h2>
              </div>

              {/* Connected Tracking Steps */}
              <div className="space-y-6 relative pl-6 before:absolute before:left-2.5 before:top-2 before:bottom-2 before:w-0.5 before:bg-slate-200/60">
                <div className="relative flex gap-3 animate-fade-in-up">
                  <div className="absolute -left-6 w-5 h-5 rounded-full flex items-center justify-center border-4 border-white bg-emerald-500 text-white shadow-sm">
                    <Check className="w-2.5 h-2.5 stroke-[4]" />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-slate-800">
                      Account Setup Complete
                    </h4>
                    <p className="text-[10px] text-slate-400 mt-0.5 leading-relaxed font-medium">
                      Merchant keys generated and sandbox profiles initialized.
                    </p>
                  </div>
                </div>

                <div className="relative flex gap-3 animate-fade-in-up animate-delay-100">
                  <div className="absolute -left-6 w-5 h-5 rounded-full flex items-center justify-center border-4 border-white bg-emerald-500 text-white shadow-sm">
                    <Check className="w-2.5 h-2.5 stroke-[4]" />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-slate-800">
                      Identity KYC Cleared
                    </h4>
                    <p className="text-[10px] text-slate-400 mt-0.5 leading-relaxed font-medium">
                      All government legal filings and identity parameters
                      verified.
                    </p>
                  </div>
                </div>

                <div className="relative flex gap-3 animate-fade-in-up animate-delay-200">
                  <div
                    className={`absolute -left-6 w-5 h-5 rounded-full flex items-center justify-center border-4 border-white shadow-sm ${
                      profile.status?.toLowerCase() === "active" ||
                      profile.status?.toLowerCase() === "approved"
                        ? "bg-emerald-500 text-white animate-pulse"
                        : "bg-amber-500 text-white animate-pulse"
                    }`}
                  >
                    {profile.status?.toLowerCase() === "active" ||
                    profile.status?.toLowerCase() === "approved" ? (
                      <Check className="w-2.5 h-2.5 stroke-[4]" />
                    ) : (
                      <div className="w-1 h-1 rounded-full bg-white" />
                    )}
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-slate-800">
                      Live Payout Terminal
                    </h4>
                    <p className="text-[10px] text-slate-400 mt-0.5 leading-relaxed font-medium">
                      Routing settlements & webhook callbacks in live production
                      modes.
                    </p>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-slate-100 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-500 flex-shrink-0">
                  <Award className="w-4 h-4" />
                </div>
                <div>
                  <h5 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                    KYC Progress State
                  </h5>
                  <p className="text-xs font-extrabold text-slate-800 mt-0.5">
                    {{
                      1: "General Info",
                      2: "Address",
                      3: "Bank Details",
                      4: "KYC Documents",
                    }[profile.step] || "Completed"}
                  </p>
                </div>
              </div>
            </div>

            {/* DIRECT SYSTEM METRICS */}
            <div className="glass-panel rounded-3xl border border-slate-100 p-5 space-y-4 relative overflow-hidden">
              <div className="flex items-center gap-3 p-3 bg-slate-50/50 rounded-2xl border border-slate-100/50">
                <div className="w-9 h-9 rounded-xl bg-violet-50 flex items-center justify-center text-violet-500 flex-shrink-0">
                  <Lock className="w-4 h-4" />
                </div>
                <div>
                  <h5 className="text-[9px] font-extrabold text-slate-400 uppercase tracking-widest">
                    Encryption Method
                  </h5>
                  <p className="text-xs font-bold text-slate-700 mt-0.5">
                    AES-256 Symmetric Secure
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 bg-slate-50/50 rounded-2xl border border-slate-100/50">
                <div className="w-9 h-9 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-500 flex-shrink-0">
                  <Globe className="w-4 h-4" />
                </div>
                <div>
                  <h5 className="text-[9px] font-extrabold text-slate-400 uppercase tracking-widest">
                    Business Network
                  </h5>
                  <p className="text-xs font-bold text-slate-700 mt-0.5">
                    Global Payout Gateway
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT SETTINGS PANEL WORKSPACE */}
          <div className="lg:col-span-8 space-y-6">
            {/* Custom Settings Tabs Bar */}
            <div className="flex flex-wrap gap-1.5 p-1 bg-slate-200/30 backdrop-blur-md rounded-2xl border border-white/60 shadow-inner">
              {tabs.map((tab, idx) => {
                const Icon = tab.icon;
                const isActive = activeTab === idx;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(idx)}
                    className={`flex items-center gap-2 px-5 py-3 rounded-xl text-xs font-bold transition duration-300 ${
                      isActive
                        ? "bg-gradient-to-r from-indigo-600 to-violet-600 text-white shadow-md shadow-indigo-500/20"
                        : "text-slate-500 hover:text-slate-800 hover:bg-white/40"
                    }`}
                  >
                    <Icon
                      className={`w-3.5 h-3.5 ${isActive ? "animate-pulse" : "opacity-80"}`}
                    />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </div>

            {/* Active Workspace Card */}
            <div className="glass-panel rounded-3xl border border-slate-100 p-6 relative overflow-hidden transition-all duration-500 min-h-[350px]">
              {/* Subtle top decoration gradient line */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-violet-600 via-indigo-500 to-indigo-600"></div>

              {/* TAB 0: BUSINESS OVERVIEW */}
              {activeTab === 0 && (
                <div className="space-y-6 animate-fade-in-up">
                  <div>
                    <h3 className="text-lg font-extrabold text-slate-800">
                      Business Details
                    </h3>
                    <p className="text-slate-450 text-xs mt-1 font-medium">
                      General firm identity, registration types, and verified
                      operating locations.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InfoItem
                      label="Legal Company Name"
                      value={profile.business_name}
                    />
                    <InfoItem
                      label="Business Type"
                      value={businessTypeLabels[profile.business_type] || "-"}
                    />
                    <InfoItem
                      label="Industry Category"
                      value={businessCategoryLabels[profile.business_category] || "-"}
                    />
                    <InfoItem label="Country" value={profile.country} />
                    <InfoItem label="State" value={profile.state} />
                    <InfoItem label="City" value={profile.city} />
                    <InfoItem label="Pincode" value={profile.pincode} />
                    <InfoItem
                      label="Operating Address"
                      value={profile.address || profile.address1}
                      copyKey="address"
                      copiedField={copiedField}
                      onCopy={handleCopy}
                    />
                  </div>

                  {profile.business_description && (
                    <div className="p-4 bg-indigo-50/20 rounded-2xl border border-indigo-100/50 mt-4">
                      <h4 className="text-[10px] font-bold text-indigo-500 uppercase tracking-wider mb-2">
                        Business Description
                      </h4>
                      <p className="text-xs text-slate-650 italic leading-relaxed">
                        "{profile.business_description}"
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* TAB 1: COMPLIANCE & LEGAL IDENTIFIERS */}
              {activeTab === 1 && (
                <div className="space-y-6 animate-fade-in-up">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                      <h3 className="text-lg font-extrabold text-slate-800">
                        Compliance & Registration
                      </h3>
                      <p className="text-slate-450 text-xs mt-1 font-medium">
                        Securely verified identification certificates and tax
                        registrations.
                      </p>
                    </div>

                    <span className="self-start md:self-auto inline-flex items-center gap-1.5 px-3 py-1 rounded-xl text-xs font-bold bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 shadow-sm shadow-emerald-50">
                      <Lock className="w-3 h-3 text-emerald-500" />
                      <span>Encrypted Database</span>
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InfoItem
                      label="PAN Card Number"
                      value={profile.pan_number}
                      copyKey="pan"
                      copiedField={copiedField}
                      onCopy={handleCopy}
                    />
                    <InfoItem
                      label="GST Tax Number"
                      value={profile.gst_number}
                      copyKey="gst"
                      copiedField={copiedField}
                      onCopy={handleCopy}
                    />
                    <InfoItem
                      label="CIN Registration Number"
                      value={profile.cin_number}
                      copyKey="cin"
                      copiedField={copiedField}
                      onCopy={handleCopy}
                    />
                    <InfoItem
                      label="TAN Ledger Number"
                      value={profile.tan_number}
                      copyKey="tan"
                      copiedField={copiedField}
                      onCopy={handleCopy}
                    />
                    {/* <InfoItem
                      label="Aadhar Document Reference"
                      value={profile.aadhar_number}
                      copyKey="aadhar"
                      copiedField={copiedField}
                      onCopy={handleCopy}
                    /> */}
                  </div>

                  {/* Warning Guidelines Note */}
                  <div className="flex gap-3 p-4 bg-slate-50 rounded-2xl border border-slate-200/60 mt-4 items-start">
                    <ShieldAlert className="w-5 h-5 text-indigo-500 flex-shrink-0 mt-0.5" />
                    <div className="space-y-1">
                      <h4 className="text-xs font-bold text-slate-800">
                        Identity Credential Lock
                      </h4>
                      <p className="text-[10px] text-slate-500 leading-relaxed font-medium">
                        These credentials have been verified by third-party
                        government database APIs. To make modifications or
                        submit new documents, please initiate a ticket via
                        support portals or send compliance updates to our
                        helpdesk.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 2: AUTHORIZED REPRESENTATIVE DIRECTORY */}
              {activeTab === 2 && (
                <div className="space-y-6 animate-fade-in-up">
                  <div>
                    <h3 className="text-lg font-extrabold text-slate-800">
                      Representative Details
                    </h3>
                    <p className="text-slate-450 text-xs mt-1 font-medium">
                      Primary contact details of the official director and
                      registered communication channels.
                    </p>
                  </div>

                  {/* Representative Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InfoItem
                      label="Authorized Director / Officer"
                      value={profile.contact_name}
                    />
                    <InfoItem
                      label="Designated Contact Email"
                      value={profile.contact_email}
                      copyKey="contact_email"
                      copiedField={copiedField}
                      onCopy={handleCopy}
                    />
                    <InfoItem
                      label="Contact Mobile Number"
                      value={
                        profile.contact_number
                          ? `+91 ${profile.contact_number}`
                          : ""
                      }
                      copyKey="contact_phone"
                      copiedField={copiedField}
                      onCopy={handleCopy}
                    />
                    <InfoItem
                      label="Alternate Business Representative"
                      value={profile.business_cname}
                    />
                    <InfoItem
                      label="Business Notification Email"
                      value={profile.contact_email}
                      copyKey="business_email"
                      copiedField={copiedField}
                      onCopy={handleCopy}
                    />
                  </div>

                  {/* Fast Contact Channels Visual Cards */}
                  <div className="pt-4 border-t border-slate-100">
                    <h4 className="text-xs font-bold text-slate-800 mb-3">
                      Primary Alert Channels
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <a
                        href={`mailto:${profile.contact_email}`}
                        className="flex items-center gap-3 p-4 rounded-2xl border border-slate-200/60 bg-slate-50/50 hover:bg-indigo-50/30 hover:border-indigo-200 transition duration-200 animate-fade-in-up"
                      >
                        <div className="w-8 h-8 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-500">
                          <Mail className="w-4 h-4" />
                        </div>
                        <div>
                          <p className="text-xs font-extrabold text-slate-700">
                            Official Mailbox
                          </p>
                          <p className="text-[10px] text-slate-400 mt-0.5">
                            Send billing queries
                          </p>
                        </div>
                      </a>

                      <a
                        href={`tel:${profile.contact_number}`}
                        className="flex items-center gap-3 p-4 rounded-2xl border border-slate-200/60 bg-slate-50/50 hover:bg-indigo-50/30 hover:border-indigo-200 transition duration-200 animate-fade-in-up animate-delay-100"
                      >
                        <div className="w-8 h-8 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-500">
                          <Phone className="w-4 h-4" />
                        </div>
                        <div>
                          <p className="text-xs font-extrabold text-slate-700">
                            Phone Support Line
                          </p>
                          <p className="text-[10px] text-slate-400 mt-0.5">
                            Registered voice alert line
                          </p>
                        </div>
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default Profile;
